import React, { useRef, useState, useCallback, useEffect } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  Flag,
  isSome,
  isNone,
  noop,
  useFlag,
  v2_useGateDeliverySuggestions,
  v2_useTrainStationDeliverySuggestions,
} from '@crocodille/common';
import styles from './shipping-selection.module.scss';
import { ShippingType } from '../../../routing';
import BBCourierIcon from '../../icons/bb-courier-icon';
import BBPickupIcon from '../../icons/bb-pickup-icon';
import ShippingCourierForm from '../shipping-courier-form/shipping-courier-form';
import Modal from '../../elements/modal/modal';
import ShippingInRestaurantForm from '../shipping-in-restaurant-form/shipping-in-restaurant-form';
import { useGeolocation } from '@hooks/use-geolocation';
import ShippingGateDeliveryForm from '../shipping-gate-delivery-form/shipping-gate-delivery-form';
import GateDeliveryIcon from '@components/icons/gate-delivery-icon';
import { useMedia } from '@hooks/use-media';
import ShippingInRestaurantModal from '../shipping-in-restaurant-modal/shipping-in-restaurant-modal';
import ShippingTrainStationForm from '../shipping-train-station-form/shipping-train-station-form';
import { useRouter } from '@framework/router';
import * as routing from '@routing';

export interface ShippingSelectionProps {
  readonly selectedShipping: ShippingType | null;
  readonly onSelectedShippingChange?: (shipping: ShippingType) => void;
  readonly navigateToProductList?: () => void;
  readonly isMainLayout: boolean;
}

const Tab: React.FC<
  React.PropsWithChildren<{
    readonly label: string;
    readonly subLabel: string;
    readonly value: string;
    readonly selected: boolean;
    onChange(value: string): void;
    readonly isModal?: boolean;
    readonly isMainLayout?: boolean;
    readonly isLarge?: boolean;
  }>
> = ({
  children,
  label,
  subLabel,
  value,
  selected,
  onChange,
  isModal,
  isMainLayout,
  isLarge,
}) => {
  return (
    <div
      className={cn(styles.tab, {
        [styles.isSelected]: selected,
        [styles.isModal]: isModal,
        [styles.isMainLayout]: isMainLayout,
        [styles.large]: isLarge,
      })}
      onClick={() => onChange(value)}
    >
      {children}
      <div className={styles.label}>
        {label}
        <span
          className={cn(styles.subLabel, {
            [styles.isMainLayout]: isMainLayout,
          })}
        >
          {subLabel}
        </span>
      </div>
    </div>
  );
};

const ShippingSelection: React.FC<
  React.PropsWithChildren<ShippingSelectionProps>
> = ({
  selectedShipping,
  onSelectedShippingChange,
  navigateToProductList = noop,
  isMainLayout,
}) => {
  const { t } = useTranslation();
  const isInWebView = useFlag(Flag.IS_IN_WEBVIEW);
  const { push } = useRouter();

  const { data: location, isValidating: locationIsPending } = useGeolocation();
  const { data: gateDeliverySuggestions } = v2_useGateDeliverySuggestions(
    !locationIsPending ? location?.coords : null,
  );
  const {
    data: trainStationDeliverySuggestions,
    isLoading: trainStationLoading,
  } = v2_useTrainStationDeliverySuggestions(
    !locationIsPending ? location?.coords : null,
  );
  const isMobile = useMedia('shipping-page-shipping-section', true);
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [isCourierFormFocused, setIsCourierFormFocused] =
    useState<boolean>(false);

  const contentRef = useRef<HTMLDivElement>(null);

  const handleTabChange = useCallback(
    (shipping: ShippingType) => {
      onSelectedShippingChange?.(shipping);
      setActiveModal(shipping);
    },
    [onSelectedShippingChange],
  );

  const handleCloseShippingInRestaurant = () => {
    handleTabChange('courier');
  };

  const handleCourierShippingSelect = () => {
    setIsCourierFormFocused(true);
    handleCloseShippingInRestaurant();
  };

  useEffect(() => {
    if (
      selectedShipping === 'train-station' &&
      !trainStationLoading &&
      isNone(trainStationDeliverySuggestions)
    ) {
      push(
        routing.shippingRoute({
          shipping: 'courier',
        }),
      );
    }
  }, [
    trainStationDeliverySuggestions,
    push,
    selectedShipping,
    trainStationLoading,
  ]);

  return (
    <div className={styles.container} ref={contentRef}>
      <div
        className={cn(styles.switch, {
          [styles.isInWebView]: isInWebView,
          [styles.isMainLayout]: isMainLayout,
        })}
      >
        <Tab
          label={
            isSome(gateDeliverySuggestions)
              ? t('shipping.gate_delivery')
              : isMobile
              ? t('shipping.courier_short')
              : t('shipping.courier')
          }
          subLabel={
            isSome(gateDeliverySuggestions)
              ? isMobile
                ? t('shipping.gate_delivery_time_short')
                : t('shipping.gate_delivery_time')
              : isMobile
              ? t('shipping.courier_time_short')
              : t('shipping.courier_time')
          }
          value="courier"
          selected={selectedShipping === 'courier'}
          onChange={handleTabChange as (value: ShippingType) => void}
          isMainLayout={isMainLayout}
          isLarge={isNone(trainStationDeliverySuggestions)}
        >
          {isSome(gateDeliverySuggestions) ? (
            <GateDeliveryIcon className={styles.tabIcon} />
          ) : (
            <BBCourierIcon className={styles.tabIcon} />
          )}
        </Tab>
        {isSome(trainStationDeliverySuggestions) ? (
          <Tab
            label={t(
              isMobile
                ? 'shipping.train_station_delivery_short'
                : 'shipping.train_station_delivery',
            )}
            subLabel={
              isMobile
                ? t('shipping.train_station_delivery_time_short')
                : t('shipping.train_station_delivery_time')
            }
            value="train-station"
            selected={selectedShipping === 'train-station'}
            onChange={handleTabChange as (value: ShippingType) => void}
            isMainLayout={isMainLayout}
          >
            <GateDeliveryIcon className={styles.tabIcon} />
          </Tab>
        ) : null}
        <Tab
          label={t(
            isMobile
              ? 'shipping.in_restaurant_short'
              : 'shipping.in_restaurant',
          )}
          subLabel={
            isMobile
              ? t('shipping.in_restaurant_time_short')
              : t('shipping.in_restaurant_time')
          }
          value="in-restaurant"
          selected={selectedShipping === 'in-restaurant'}
          onChange={handleTabChange as (value: ShippingType) => void}
          isMainLayout={isMainLayout}
          isLarge={isNone(trainStationDeliverySuggestions)}
        >
          <BBPickupIcon className={styles.tabIcon} />
        </Tab>
      </div>
      <div
        className={cn(styles.content, { [styles.isMainLayout]: isMainLayout })}
      >
        {selectedShipping === 'courier' ? (
          isSome(gateDeliverySuggestions) ? (
            <>
              <ShippingGateDeliveryForm
                navigateToProductList={navigateToProductList}
                suggestions={gateDeliverySuggestions}
              />
            </>
          ) : (
            <>
              <ShippingCourierForm
                navigateToProductList={navigateToProductList}
                isFormFocused={isCourierFormFocused}
              />
            </>
          )
        ) : isSome(trainStationDeliverySuggestions) ? (
          <ShippingTrainStationForm
            suggestions={trainStationDeliverySuggestions}
            navigateToProductList={navigateToProductList}
          />
        ) : null}

        {isMobile ? (
          <Modal
            title={t('shipping.in_restaurant')}
            open={activeModal === 'in-restaurant'}
            onClose={
              isMainLayout
                ? () => setActiveModal(null)
                : handleCloseShippingInRestaurant
            }
            showCloseButton={true}
            hideTitle={true}
            width="fill"
            height="fill"
            inverseButton
            className={styles.noBorder}
          >
            <ShippingInRestaurantForm
              navigateToProductList={navigateToProductList}
            />
          </Modal>
        ) : (
          <ShippingInRestaurantModal
            open={selectedShipping === 'in-restaurant'}
            onClose={handleCloseShippingInRestaurant}
            navigateToProductList={navigateToProductList}
          >
            <div
              className={cn(styles.switch, {
                [styles.isInWebView]: isInWebView,
              })}
            >
              <Tab
                label={
                  isSome(gateDeliverySuggestions)
                    ? t('shipping.gate_delivery')
                    : isMobile
                    ? t('shipping.courier_short')
                    : t('shipping.courier')
                }
                subLabel={
                  isSome(gateDeliverySuggestions)
                    ? isMobile
                      ? t('shipping.gate_delivery_time_short')
                      : t('shipping.gate_delivery_time')
                    : isMobile
                    ? t('shipping.courier_time_short')
                    : t('shipping.courier_time')
                }
                value="courier"
                selected={false}
                onChange={handleCourierShippingSelect}
                isModal
              >
                {isSome(gateDeliverySuggestions) ? (
                  <GateDeliveryIcon className={styles.tabIcon} />
                ) : (
                  <BBCourierIcon className={styles.tabIcon} />
                )}
              </Tab>
              {isSome(trainStationDeliverySuggestions) ? (
                <Tab
                  label={t(
                    isMobile
                      ? 'shipping.train_station_delivery_short'
                      : 'shipping.train_station_delivery',
                  )}
                  subLabel={
                    isMobile
                      ? t('shipping.train_station_delivery_time_short')
                      : t('shipping.train_station_delivery_time')
                  }
                  value="train-station"
                  selected={false}
                  onChange={handleTabChange as (value: ShippingType) => void}
                  isModal
                >
                  <GateDeliveryIcon className={styles.tabIcon} />
                </Tab>
              ) : null}
              <Tab
                label={t(
                  isMobile
                    ? 'shipping.in_restaurant_short'
                    : 'shipping.in_restaurant',
                )}
                subLabel={
                  isMobile
                    ? t('shipping.in_restaurant_time_short')
                    : t('shipping.in_restaurant_time')
                }
                value="in-restaurant"
                selected
                onChange={handleTabChange as (value: ShippingType) => void}
                isModal
              >
                <BBPickupIcon className={styles.tabIcon} />
              </Tab>
            </div>
          </ShippingInRestaurantModal>
        )}

        <Modal
          open={activeModal === 'courier' && isMobile && isMainLayout}
          onClose={() => setActiveModal(null)}
          closeOnOutsideClick={true}
          title={
            isSome(gateDeliverySuggestions)
              ? t('shipping.gate_delivery')
              : t('shipping.courier')
          }
          showCloseButton={true}
          width="auto"
          transparent
          titleIcon={
            isSome(gateDeliverySuggestions) ? (
              <GateDeliveryIcon />
            ) : (
              <BBCourierIcon />
            )
          }
          dialogClassName={styles.modalMargin}
        >
          <div className={styles.shippingCourierContent}>
            {isSome(gateDeliverySuggestions) ? (
              <>
                <ShippingGateDeliveryForm
                  navigateToProductList={navigateToProductList}
                  suggestions={gateDeliverySuggestions}
                />
              </>
            ) : (
              <>
                <ShippingCourierForm
                  navigateToProductList={navigateToProductList}
                />
              </>
            )}
            <div className={styles.backlog}></div>
          </div>
        </Modal>
        {isSome(trainStationDeliverySuggestions) ? (
          <Modal
            open={activeModal === 'train-station' && isMobile && isMainLayout}
            onClose={() => setActiveModal(null)}
            closeOnOutsideClick={true}
            title={t('shipping.train_station_delivery')}
            showCloseButton={true}
            width="auto"
            transparent
            titleIcon={<BBCourierIcon />}
            dialogClassName={styles.modalMargin}
          >
            <div className={styles.shippingCourierContent}>
              <ShippingTrainStationForm
                suggestions={trainStationDeliverySuggestions}
                navigateToProductList={navigateToProductList}
              />
              <div className={styles.backlog}></div>
            </div>
          </Modal>
        ) : null}
      </div>
    </div>
  );
};

export default ShippingSelection;
